import React from 'react';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

interface Milestone {
  id: number;
  title: string;
  description: string;
  dueDate: string;
  deliverable: string;
  status: 'pending' | 'in_progress' | 'completed';
}

interface MilestoneTrackerProps {
  milestones: Milestone[];
}

const MilestoneTracker: React.FC<MilestoneTrackerProps> = ({ milestones }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'in_progress':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completato';
      case 'in_progress':
        return 'In corso';
      case 'pending':
        return 'In attesa';
      default:
        return 'Non iniziato';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Milestone di Progetto</h2>
      
      <div className="space-y-6">
        {milestones.map((milestone, index) => (
          <div key={milestone.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-grow">
                <div className="flex items-center mb-2">
                  {getStatusIcon(milestone.status)}
                  <h3 className="ml-2 text-lg font-semibold text-gray-900">
                    {milestone.title}
                  </h3>
                </div>
                
                <p className="text-gray-600 mb-3">
                  {milestone.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Scadenza:</span>
                    <p className="text-sm text-gray-900 font-medium">{milestone.dueDate}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Deliverable:</span>
                    <p className="text-sm text-gray-900">{milestone.deliverable}</p>
                  </div>
                </div>
              </div>
              
              <div className="ml-4 flex-shrink-0">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(milestone.status)}`}>
                  {getStatusText(milestone.status)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Summary */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              {milestones.filter(m => m.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-500">Completati</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-yellow-600">
              {milestones.filter(m => m.status === 'in_progress').length}
            </div>
            <div className="text-sm text-gray-500">In corso</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-600">
              {milestones.filter(m => m.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-500">Rimanenti</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilestoneTracker;